"use client";

import Magnetic from "@/components/ui/Magnetic";
import { trackClarityEvent } from "@/lib/clarity-event";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Brain, Briefcase, Building2, Globe, Mail, MapPin, Rocket, Sparkles, Terminal } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { FaInstagram, FaLinkedin } from "react-icons/fa6";
import styles from "./Navbar.module.css";

const patternClassMap: Record<string, string> = {
    "Concept Lab": styles.bgTechPattern,
    "Superpowers": styles.bgSuperpowerPattern,
    "Our Work": styles.bgWorkPattern,
    "Company": styles.bgAboutPattern
};

const superpowersItems = [
    {
        title: "AI-Accelerated MVPs",
        desc: "Go from concept to launch in weeks with hybrid agentic pipelines.",
        href: "/services#mvp",
        icon: Rocket,
        color: "text-blue-600 bg-blue-50 border border-blue-100"
    },
    {
        title: "AI-Augmented Teams",
        desc: "Elite engineers paired with advanced AI co-pilots for 10x velocity.",
        href: "/services#staffing",
        icon: Globe,
        color: "text-emerald-600 bg-emerald-50 border border-emerald-100"
    },
    {
        title: "Agentic Workflows",
        desc: "Deploy custom cognitive state-graphs and fine-tuned LLMs.",
        href: "/services#agentic",
        icon: Brain,
        color: "text-purple-600 bg-purple-50 border border-purple-100"
    }
];

const conceptLabItems = [
    {
        title: "Idea Validator",
        desc: "Submit your concept to receive direct strategic AI feedback.",
        href: "/ai-validator",
        icon: Sparkles,
        color: "text-amber-600 bg-amber-50 border border-amber-100"
    },
    {
        title: "Interactive Terminal",
        desc: "Press ~ to experience our custom engineering workspace.",
        href: "#",
        icon: Terminal,
        onClick: () => {
            window.dispatchEvent(new CustomEvent("open-dev-terminal"));
        },
        color: "text-gray-900 bg-gray-50 border border-gray-200"
    }
];

const companyItems = [
    {
        title: "About Us",
        desc: "Our story, values, and vision for AI-native engineering.",
        href: "/about",
        icon: Building2,
        color: "text-indigo-600 bg-indigo-50 border border-indigo-100"
    },
    {
        title: "Careers",
        desc: "Join our team and build the future of autonomous systems.",
        href: "/careers",
        icon: Briefcase,
        color: "text-lime-600 bg-lime-50 border border-lime-100"
    },
    {
        title: "Insights",
        desc: "Stay ahead with our latest research, news, and updates.",
        href: "/blogs",
        icon: BookOpen,
        color: "text-rose-600 bg-rose-50 border border-rose-100"
    }
];

export default function Navbar() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const pathname = usePathname();

    const logoClicks = useRef(0);
    const clickTimeout = useRef<NodeJS.Timeout | null>(null);
    const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleMouseEnter = (menuName: string) => {
        if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
        if (menuName === "Superpowers" || menuName === "Concept Lab" || menuName === "Company") {
            setActiveMenu(menuName);
        } else {
            setActiveMenu(null);
        }
    };

    const handleMouseLeave = () => {
        menuTimeoutRef.current = setTimeout(() => {
            setActiveMenu(null);
        }, 180);
    };

    const navLinks = [
        { name: "Superpowers", href: "/services" },
        { name: "Our Work", href: "/case-studies" },
        { name: "Concept Lab", href: "/ai-validator" },
        { name: "Company", href: "/about" },
    ];

    return (
        <>
            {/* Desktop dynamic floating navbar */}
            <motion.header
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className={`fixed top-0 left-0 right-0 z-50 flex flex-col items-center transition-all duration-300 pointer-events-none ${scrolled ? "pt-4" : "pt-6"}`}
            >
                <div
                    className={`pointer-events-auto bg-white/70 backdrop-blur-2xl backdrop-saturate-150 border border-zinc-200/80 shadow-xl transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] flex items-center justify-between px-2 py-1 sm:px-3 sm:py-2
                    ${scrolled
                            ? "w-[95%] sm:w-[90%] md:w-[820px] rounded-full h-16"
                            : "w-[95%] md:w-[900px] rounded-full h-20"
                        }
                    `}
                >
                    {/* Logo */}
                    <Link
                        href="/"
                        className="flex items-center space-x-2 pl-2 sm:pl-4 group shrink-0"
                        onClick={(e) => {
                            if (clickTimeout.current) clearTimeout(clickTimeout.current);
                            logoClicks.current += 1;
                            if (logoClicks.current >= 5) {
                                window.dispatchEvent(new CustomEvent("trigger-logo-physics"));
                                logoClicks.current = 0;
                            } else {
                                clickTimeout.current = setTimeout(() => {
                                    logoClicks.current = 0;
                                }, 500);
                            }
                        }}
                    >
                        <div className="relative w-8 h-8 rounded-full flex items-center justify-center overflow-hidden transition-transform duration-300 group-hover:scale-110 active:scale-95">
                            <Image src="/logo-black-64.png" alt="Logo" width={30} height={30} className="object-contain" />
                        </div>
                        <span className={`font-heading tracking-tight transition-all duration-300 block ${scrolled ? "sm:w-0 sm:opacity-0 sm:overflow-hidden" : "w-auto opacity-100"}`}>
                            <span className="font-semibold text-zinc-900">Dharma</span>
                            <span className="font-light text-zinc-500">Tech</span>
                        </span>
                    </Link>

                    {/* Desktop Links */}
                    <nav className="hidden md:flex items-center gap-1.5" onMouseLeave={handleMouseLeave}>
                        {navLinks.map((link) => (
                            <Magnetic key={link.name}>
                                <Link
                                    href={link.href}
                                    className={`relative px-4 py-2 text-[15px] font-heading font-semibold tracking-wide transition-colors group whitespace-nowrap
                                        ${link.name === "Superpowers" ? "text-gray-600" :
                                            link.name === "Concept Lab" ? "text-gray-600" :
                                                link.name === "Company" ? "text-gray-600" :
                                                    "text-gray-600 hover:text-black"}
                                    `}
                                    onMouseEnter={() => handleMouseEnter(link.name)}
                                    onClick={() => {
                                        setActiveMenu(null);
                                        trackClarityEvent(`nav_click_${link.name.toLowerCase().replace(' ', '_')}`);
                                    }}
                                >
                                    <span className={`relative z-10 inline-block transition-all duration-200 
                                        ${link.name === "Superpowers"
                                            ? "group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:via-cyan-400 group-hover:to-blue-600 group-hover:italic group-hover:-skew-x-12 group-hover:scale-105 group-hover:animate-text-shimmer"
                                            : link.name === "Concept Lab"
                                                ? "group-hover:text-emerald-500 group-hover:font-mono group-hover:tracking-tighter"
                                                : link.name === "Our Work"
                                                    ? "group-hover:text-orange-500 group-hover:tracking-widest group-hover:font-bold"
                                                    : link.name === "Company"
                                                        ? "group-hover:text-indigo-600 group-hover:-rotate-3 group-hover:scale-110"
                                                        : "group-hover:text-black"
                                        }`}>
                                        {link.name}
                                    </span>
                                    {pathname === link.href && (
                                        <motion.div
                                            layoutId="navbar-indicator"
                                            className={`absolute inset-0 rounded-full -z-10 ${patternClassMap[link.name] || "bg-gray-100"}`}
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}
                                    <span className={`absolute -bottom-1 left-1/2 w-0 h-0.5 transition-all group-hover:w-1/2 -translate-x-1/2 
                                        ${link.name === "Superpowers" ? "bg-cyan-500" :
                                            link.name === "Concept Lab" ? "bg-emerald-500" :
                                                link.name === "Our Work" ? "bg-orange-500" :
                                                    link.name === "Company" ? "bg-indigo-600" :
                                                        "bg-black"}`} />
                                </Link>
                            </Magnetic>
                        ))}
                    </nav>

                    {/* Right Actions */}
                    <div className="flex items-center gap-2 pr-2 shrink-0">
                        <span className="hidden sm:inline-block">
                            <Magnetic>
                                <Link
                                    href="/contact"
                                    className="bg-black text-white px-5 py-2.5 rounded-full text-[15px] font-heading font-bold tracking-wide hover:scale-105 active:scale-95 transition-all flex items-center shadow-lg hover:shadow-xl"
                                    onClick={() => trackClarityEvent('nav_click_lets_talk')}
                                >
                                    <span className="whitespace-nowrap">Let&apos;s Talk</span>
                                </Link>
                            </Magnetic>
                        </span>



                        {/* Mobile Toggle */}
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="md:hidden relative w-10 h-10 text-gray-600 hover:text-black bg-zinc-50/85 backdrop-blur-md border border-zinc-200/50 shadow-sm hover:bg-zinc-100 rounded-full transition-all duration-300 flex items-center justify-center z-50 hover:scale-105 active:scale-95"
                            aria-label="Toggle Menu"
                        >
                            <div className="flex flex-col gap-[6px] w-5 items-center justify-center">
                                <span
                                    className={`block w-5 h-[2px] bg-current transition-all duration-300 ease-out origin-center ${
                                        isOpen ? "rotate-45 translate-y-[4px]" : ""
                                    }`}
                                />
                                <span
                                    className={`block w-5 h-[2px] bg-current transition-all duration-300 ease-out origin-center ${
                                        isOpen ? "-rotate-45 -translate-y-[4px]" : ""
                                    }`}
                                />
                            </div>
                        </button>
                    </div>
                </div>

                {/* Mega Menu Dropdown */}
                <AnimatePresence>
                    {activeMenu && (
                        <motion.div
                            layout="size"
                            initial={{ opacity: 0, y: -10, scale: 0.99 }}
                            animate={{ opacity: 1, y: 2, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.99 }}
                            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                            className={`pointer-events-auto bg-white/90 backdrop-blur-2xl border border-zinc-200/80 shadow-2xl rounded-[2rem] overflow-hidden p-6 z-40 transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]
                                ${scrolled
                                    ? "w-[95%] sm:w-[90%] md:w-[820px]"
                                    : "w-[95%] md:w-[900px]"
                                }
                            `}
                            onMouseEnter={() => { if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current); }}
                            onMouseLeave={handleMouseLeave}
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.div
                                    key={activeMenu}
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -5 }}
                                    transition={{ duration: 0.15, ease: "easeInOut" }}
                                >
                                    {activeMenu === "Superpowers" && (
                                        <div className="grid grid-cols-3 gap-4">
                                            {superpowersItems.map((item, i) => (
                                                <Link
                                                    key={i}
                                                    href={item.href}
                                                    className="group flex flex-col p-4 rounded-2xl hover:bg-neutral-50 border border-transparent hover:border-neutral-100 transition-all duration-300"
                                                    onClick={() => {
                                                        setActiveMenu(null);
                                                        trackClarityEvent(`mega_menu_click_${item.title.toLowerCase().replace(' ', '_')}`);
                                                    }}
                                                >
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110 ${item.color}`}>
                                                        <item.icon className="w-5 h-5" />
                                                    </div>
                                                    <h4 className="text-sm font-heading font-bold text-gray-900 group-hover:text-black mb-1">
                                                        {item.title}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 leading-relaxed font-mono">
                                                        {item.desc}
                                                    </p>
                                                </Link>
                                            ))}
                                        </div>
                                    )}

                                    {activeMenu === "Concept Lab" && (
                                        <div className="grid grid-cols-2 gap-4">
                                            {conceptLabItems.map((item, i) => {
                                                const CardContent = (
                                                    <div className="group flex p-4 rounded-2xl hover:bg-neutral-50 border border-transparent hover:border-neutral-100 transition-all duration-300 cursor-pointer h-full">
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 shrink-0 transition-transform group-hover:scale-110 ${item.color}`}>
                                                            <item.icon className="w-5 h-5" />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <h4 className="text-sm font-heading font-bold text-gray-900 group-hover:text-black mb-1">
                                                                {item.title}
                                                            </h4>
                                                            <p className="text-xs text-gray-500 leading-relaxed font-mono">
                                                                {item.desc}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );

                                                return item.onClick ? (
                                                    <div
                                                        key={i}
                                                        onClick={() => {
                                                            setActiveMenu(null);
                                                            item.onClick();
                                                            trackClarityEvent(`mega_menu_click_${item.title.toLowerCase().replace(' ', '_')}`);
                                                        }}
                                                    >
                                                        {CardContent}
                                                    </div>
                                                ) : (
                                                    <Link
                                                        key={i}
                                                        href={item.href}
                                                        onClick={() => {
                                                            setActiveMenu(null);
                                                            trackClarityEvent(`mega_menu_click_${item.title.toLowerCase().replace(' ', '_')}`);
                                                        }}
                                                    >
                                                        {CardContent}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {activeMenu === "Company" && (
                                        <div className="grid grid-cols-3 gap-4">
                                            {companyItems.map((item, i) => (
                                                <Link
                                                    key={i}
                                                    href={item.href}
                                                    className="group flex flex-col p-4 rounded-2xl hover:bg-neutral-50 border border-transparent hover:border-neutral-100 transition-all duration-300"
                                                    onClick={() => {
                                                        setActiveMenu(null);
                                                        trackClarityEvent(`mega_menu_click_${item.title.toLowerCase().replace(' ', '_')}`);
                                                    }}
                                                >
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110 ${item.color}`}>
                                                        <item.icon className="w-5 h-5" />
                                                    </div>
                                                    <h4 className="text-sm font-heading font-bold text-gray-900 group-hover:text-black mb-1">
                                                        {item.title}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 leading-relaxed font-mono">
                                                        {item.desc}
                                                    </p>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.header>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="fixed inset-0 z-40 bg-white/95 backdrop-blur-2xl px-6 md:hidden overflow-y-auto pb-12 pt-28"
                    >
                        <div className="flex flex-col space-y-5">
                            {[
                                { name: "Superpowers", href: "/services" },
                                { name: "Our Work", href: "/case-studies" },
                                { name: "Concept Lab", href: "/ai-validator" },
                                { name: "About Us", href: "/about" },
                                { name: "Careers", href: "/careers" },
                                { name: "Insights", href: "/blogs" },
                            ].map((link, i) => {
                                const isActive = pathname === link.href;
                                const activeColorClass =
                                    link.name === "Superpowers" ? "text-blue-600" :
                                        link.name === "Concept Lab" ? "text-emerald-600" :
                                            link.name === "Our Work" ? "text-orange-500" :
                                                link.name === "About Us" ? "text-indigo-600" :
                                                    link.name === "Careers" ? "text-lime-600" :
                                                        link.name === "Insights" ? "text-rose-600" :
                                                            "text-zinc-900";

                                const dotColorClass =
                                    link.name === "Superpowers" ? "bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.6)]" :
                                        link.name === "Concept Lab" ? "bg-emerald-600 shadow-[0_0_8px_rgba(5,150,105,0.6)]" :
                                            link.name === "Our Work" ? "bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.6)]" :
                                                link.name === "About Us" ? "bg-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.6)]" :
                                                    link.name === "Careers" ? "bg-lime-600 shadow-[0_0_8px_rgba(101,163,13,0.6)]" :
                                                        link.name === "Insights" ? "bg-rose-600 shadow-[0_0_8px_rgba(225,29,72,0.6)]" :
                                                            "bg-black";

                                return (
                                    <motion.div
                                        key={link.name}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.05 * i }}
                                        className="relative"
                                    >
                                        <Link
                                            href={link.href}
                                            className={`text-2xl font-heading font-bold tracking-tight transition-all duration-300 flex items-center gap-3 py-1
                                                ${isActive ? activeColorClass : "text-zinc-400 hover:text-zinc-600"}
                                            `}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {isActive && (
                                                <motion.span
                                                    layoutId="mobile-nav-dot"
                                                    className={`w-2 h-2 rounded-full shrink-0 ${dotColorClass}`}
                                                />
                                            )}
                                            <span>{link.name}</span>
                                        </Link>
                                    </motion.div>
                                );
                            })}

                            {/* Mobile Let's Talk Button */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.05 * 7 }}
                                className="pt-4"
                            >
                                <Link
                                    href="/contact"
                                    className="w-full bg-black text-white py-3.5 px-6 rounded-full text-center text-base font-heading font-bold tracking-wide hover:bg-neutral-900 active:scale-95 transition-all flex items-center justify-center shadow-lg"
                                    onClick={() => {
                                        setIsOpen(false);
                                        trackClarityEvent('nav_click_lets_talk_mobile');
                                    }}
                                >
                                    <span>Let&apos;s Talk</span>
                                </Link>
                            </motion.div>

                            {/* Mobile Footer Info */}
                            <motion.div
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.05 * 8 }}
                                className="pt-6 border-t border-zinc-150 space-y-4"
                            >
                                {/* Direct Contact */}
                                <div className="space-y-1">
                                    <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-450">Direct Contact</p>
                                    <a href="mailto:contact@dharmatech.in" className="flex items-center gap-2 text-zinc-700 hover:text-black font-semibold text-sm transition-colors">
                                        <Mail className="w-4 h-4 text-zinc-400" />
                                        <span>contact@dharmatech.in</span>
                                    </a>
                                </div>

                                {/* Office Location */}
                                <div className="space-y-1">
                                    <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-450">Global HQ</p>
                                    <div className="flex items-start gap-2 text-xs text-zinc-500 font-mono">
                                        <MapPin className="w-3.5 h-3.5 text-zinc-450 shrink-0 mt-0.5" />
                                        <span>3rd Floor, Pran Prabha, Nanpura, Surat, India</span>
                                    </div>
                                </div>

                                {/* Social Links */}
                                <div className="flex items-center gap-3 pt-2">
                                    <a
                                        href="https://www.linkedin.com/company/dharma-tech"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-50 border border-zinc-200 text-zinc-500 hover:text-black hover:bg-zinc-100 transition-colors"
                                        aria-label="LinkedIn"
                                    >
                                        <FaLinkedin className="w-4 h-4" />
                                    </a>
                                    <a
                                        href="https://www.instagram.com/dharmatech.in/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-50 border border-zinc-200 text-zinc-500 hover:text-black hover:bg-zinc-100 transition-colors"
                                        aria-label="Instagram"
                                    >
                                        <FaInstagram className="w-4 h-4" />
                                    </a>
                                    <span className="ml-auto text-[10px] font-mono text-zinc-400">
                                        &copy; {new Date().getFullYear()} DharmaTech
                                    </span>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
